import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/cn';

/**
 * ScrollArea — an overlay-scrollbar container for panels, menus and long lists.
 *
 * Use it where a native scrollbar would eat layout width or clash with the surface.
 * Do **not** use it around a virtualised list: the virtualiser needs a real scroll
 * element it can measure, which is what <DataTable> uses.
 *
 * `fade` masks the scrolled-past edges so content dissolves into the container rather
 * than being cut by a hard line.
 */

export type ScrollAreaOrientation = 'vertical' | 'horizontal' | 'both';

export interface ScrollAreaProps extends Omit<ComponentPropsWithoutRef<'div'>, 'dir'> {
  orientation?: ScrollAreaOrientation;
  /** `hover` (default) reveals the bar on hover; `always` pins it; `auto` follows overflow. */
  type?: 'auto' | 'always' | 'scroll' | 'hover';
  viewportClassName?: string;
  viewportRef?: Ref<HTMLDivElement>;
  /** Soften the top and bottom edges of a vertical scroller. */
  fade?: boolean;
  /** Ground the fade dissolves into. Defaults to `--surface-1`. */
  fadeGround?: string;
  children?: ReactNode;
}

export const ScrollArea = forwardRef<HTMLDivElement, ScrollAreaProps>(function ScrollArea(
  {
    orientation = 'vertical',
    type = 'hover',
    className,
    viewportClassName,
    viewportRef,
    fade = false,
    fadeGround = 'var(--surface-1)',
    children,
    ...rest
  },
  ref,
) {
  return (
    <ScrollAreaPrimitive.Root
      ref={ref}
      type={type}
      scrollHideDelay={400}
      className={cn('relative min-h-0 min-w-0 overflow-hidden', className)}
      {...rest}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className={cn('size-full [&>div]:!block', viewportClassName)}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>

      {fade ? (
        <>
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 top-0 h-4"
            style={{ background: `linear-gradient(to bottom, ${fadeGround}, transparent)` }}
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-x-0 bottom-0 h-4"
            style={{ background: `linear-gradient(to top, ${fadeGround}, transparent)` }}
          />
        </>
      ) : null}

      {orientation !== 'horizontal' ? <Bar orientation="vertical" /> : null}
      {orientation !== 'vertical' ? <Bar orientation="horizontal" /> : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  );
});

function Bar({ orientation }: { orientation: 'vertical' | 'horizontal' }) {
  return (
    <ScrollAreaPrimitive.Scrollbar
      orientation={orientation}
      className={cn(
        // Mirrors the native scrollbar metrics set in base.css (10px track, 3px inset).
        'flex touch-none select-none p-[3px]',
        'transition-opacity ease-[var(--ease-out)] duration-[var(--dur-normal)]',
        'data-[state=hidden]:opacity-0',
        orientation === 'vertical' ? 'w-[10px] flex-col' : 'h-[10px] flex-row',
      )}
    >
      <ScrollAreaPrimitive.Thumb
        className={cn(
          'relative flex-1 rounded-[var(--radius-full)] bg-[var(--line-strong)]',
          'transition-colors ease-[var(--ease-out)] duration-[var(--dur-fast)] hover:bg-[var(--text-faint)]',
        )}
      />
    </ScrollAreaPrimitive.Scrollbar>
  );
}

export const ScrollAreaViewport = ScrollAreaPrimitive.Viewport;
export const ScrollAreaRoot = ScrollAreaPrimitive.Root;
